/**
 * Login Form Prefill Utility Functions
 * Manages caching user email/username credentials in browser LocalStorage
 * to improve user experience on login & registration forms.
 */

// Storage key used to persist prefill preferences in LocalStorage
const LOGIN_PREFILL_KEY = 'clipstone_login_prefill';

/**
 * Safely parses a JSON string, returning null if invalid.
 * @param {string} value - String to parse
 * @returns {Object|null} Parsed JSON object or null
 */
const safeParse = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/**
 * Saves non-empty login credentials to LocalStorage.
 * @param {Object} params - User credentials
 * @param {string} params.email - User email address
 * @param {string} [params.username] - User display name/username
 */
export const saveLoginPrefill = ({ email, username }) => {
  if (typeof window === 'undefined') return;

  const payload = {
    email: String(email || '').trim(),
    username: String(username || '').trim(),
    updatedAt: new Date().toISOString(),
  };

  // Do not store if email is blank
  if (!payload.email) return;

  window.localStorage.setItem(LOGIN_PREFILL_KEY, JSON.stringify(payload));
};

/**
 * Retrieves saved login prefill credentials from LocalStorage.
 * @returns {{ email: string, username: string }|null} Saved credentials object or null
 */
export const getLoginPrefill = () => {
  if (typeof window === 'undefined') return null;

  const raw = window.localStorage.getItem(LOGIN_PREFILL_KEY);
  const parsed = safeParse(raw || '');

  if (!parsed || typeof parsed !== 'object') return null;

  return {
    email: String(parsed.email || '').trim(),
    username: String(parsed.username || '').trim(),
  };
};

/**
 * Clears saved login prefill credentials from LocalStorage.
 */
export const clearLoginPrefill = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(LOGIN_PREFILL_KEY);
};
